import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCheck, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import { CSSProperties, forwardRef, ReactNode, useMemo } from 'react';
import {
  CircularProgress,
  IconButton,
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
} from '@mui/material';
import { normalizeSize } from '../inputs/helpers';
import { Link } from 'react-router-dom';

// @deprecated - Use MUI Variants and Colors instead
type RBVariants =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'dark'
  | 'light'
  | 'link'
  | 'ghost'
  | 'outline-primary'
  | 'outline-secondary'
  | 'outline-success'
  | 'outline-danger'
  | 'outline-warning'
  | 'outline-info'
  | 'outline-dark'
  | 'outline-light';

export interface ButtonProps {
  active?: boolean;
  disabled?: boolean;
  id?: string;
  onClick?: MuiButtonProps['onClick'];
  size?: 'sm' | 'lg' | 'small' | 'medium' | 'large';
  type?: 'button' | 'submit' | 'reset';
  variant?: RBVariants | 'text' | 'outlined' | 'contained';
  content?: ReactNode;
  isLoading?: boolean;
  icon?: FontAwesomeIconProps['icon'];
  className?: string;
  href?: string;
  target?: '_blank';
  block?: boolean;
  inlineMarginTop?: boolean;
  style?: CSSProperties;
  noWrap?: boolean;
  title?: string;
  sx?: MuiButtonProps['sx'];
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const getIcon: IconProp | undefined = useMemo(() => {
      if (props.icon) {
        return props.icon;
      }

      switch (props.type) {
        case 'submit':
          return faCheck;
        case 'reset':
          return faRotateLeft;
        default:
          return undefined;
      }
    }, [props]);

    const muiSize = normalizeSize(props.size || 'medium');

    const muiVariant = useMemo(() => {
      switch (props.variant) {
        // Outlined variants
        case 'outline-primary':
        case 'outline-secondary':
        case 'outline-success':
        case 'outline-danger':
        case 'outline-warning':
        case 'outline-info':
        case 'outline-dark':
        case 'outline-light':
        case 'outlined':
          return 'outlined';
        // Text variants
        case 'text':
        case 'ghost':
        case 'link':
          return 'text';
        // Contained variants (default for all solid colors)
        default:
          return 'contained';
      }
    }, [props.variant]);

    const muiColor = useMemo(() => {
      switch (props.variant) {
        // Error variants (danger)
        case 'danger':
        case 'outline-danger':
          return 'error';
        // Warning variants
        case 'warning':
        case 'outline-warning':
          return 'warning';
        // Success variants
        case 'success':
        case 'outline-success':
          return 'success';
        // Info variants
        case 'info':
        case 'outline-info':
          return 'info';
        // Secondary variants
        case 'secondary':
        case 'outline-secondary':
          return 'secondary';
        // Inherit variants (for light/dark that don't have direct MUI equivalents)
        case 'light':
        case 'outline-light':
        case 'dark':
        case 'outline-dark':
          return 'inherit';
        // Primary variants (default)
        default:
          return 'primary';
      }
    }, [props.variant]);

    if (!props.content) {
      return (
        <IconButton
          ref={ref}
          disabled={props.disabled || props.isLoading}
          size={muiSize}
          color={muiColor}
          onClick={props.onClick}
          title={props.title}
          sx={{ m: 0.5, ...props.sx }}
        >
          {getIcon && <FontAwesomeIcon icon={getIcon} />}
        </IconButton>
      );
    }

    return (
      <MuiButton
        ref={ref}
        variant={muiVariant}
        color={muiColor}
        disabled={props.disabled || props.isLoading}
        size={muiSize}
        onClick={props.onClick}
        title={props.title}
        sx={{ m: 0.5, ...props.sx }}
        fullWidth={props.block}
        type={props.type || 'button'}
        startIcon={
          props.isLoading ? (
            <CircularProgress size={16} />
          ) : (
            getIcon && <FontAwesomeIcon icon={getIcon} />
          )
        }
        {...(props.href ? { component: Link, to: props.href } : {})}
      >
        {props.content}
      </MuiButton>
    );
  },
);

Button.displayName = 'Button';
